import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { LifecycleProjectWorkspace } from "./LifecycleProjectWorkspace";
import { MetricsFlightControlStrip } from "./MetricsFlightControlStrip";

export const DashboardFlightControl = () => {
  const [data, setData] = useState({ metrics: [], items: [] });
  const [activeStage, setActiveStage] = useState(null);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const analyticsTrayRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async (signal) => {
    try {
      const response = await axios.get(`${API}/dashboard/flight-control`, { signal });
      setData(response.data);
      setFailed(false);
    } catch (error) {
      if (error.code !== "ERR_CANCELED") setFailed(true);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    const interval = window.setInterval(() => load(controller.signal), 60000);
    const refresh = () => load(controller.signal);
    window.addEventListener("focus", refresh);
    return () => { controller.abort(); window.clearInterval(interval); window.removeEventListener("focus", refresh); };
  }, [load]);

  useEffect(() => {
    if (analyticsTrayRef.current) analyticsTrayRef.current.inert = !analyticsOpen;
  }, [analyticsOpen]);

  return (
    <div data-testid="dashboard-flight-control-workspace">
      <div className="mb-2 flex justify-end" data-testid="dashboard-analytics-toolbar">
        <button
          type="button"
          onClick={() => setAnalyticsOpen(open => !open)}
          aria-expanded={analyticsOpen}
          aria-controls="dashboard-analytics-tray"
          className="text-sm font-semibold text-slate-600 transition-colors duration-200 hover:text-slate-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-700 focus-visible:ring-offset-2"
          data-testid="dashboard-analytics-toggle"
        >
          {analyticsOpen ? "Hide Analytics" : "📊 View Analytics"}
        </button>
      </div>

      <div
        id="dashboard-analytics-tray"
        ref={analyticsTrayRef}
        className={`grid transition-[grid-template-rows,opacity,margin-bottom] duration-300 ease-out ${analyticsOpen ? "visible mb-3 grid-rows-[1fr] opacity-100" : "invisible mb-0 grid-rows-[0fr] opacity-0 pointer-events-none"}`}
        aria-hidden={!analyticsOpen}
        data-testid="dashboard-analytics-tray"
      >
        <div className="min-h-0 overflow-hidden">
          <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50/90 p-2 shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
            {loading ? (
              <>
                <div className="h-[104px] animate-pulse rounded-md bg-white" data-testid="dashboard-flight-control-loading" />
                <div className="rounded-md border border-slate-200 bg-white px-4 py-10 text-center text-sm text-slate-500" data-testid="dashboard-flight-control-workspace-loading">Loading pipeline workspace…</div>
              </>
            ) : failed ? (
              <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" data-testid="dashboard-flight-control-error">Analytics and pipeline workspace are temporarily unavailable.</div>
            ) : (
              <>
                <MetricsFlightControlStrip metrics={data.metrics} activeStage={activeStage} interactive={analyticsOpen} onStageChange={stage => setActiveStage(current => current === stage ? null : stage)} />
                <LifecycleProjectWorkspace items={data.items} activeStage={activeStage} onClear={() => setActiveStage(null)} flushTop />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};